import { HttpContextToken, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { finalize } from 'rxjs/operators';
import { LoaderMessageService } from './loader-message.service';

/**
 * Opt a single request out of the global loading overlay.
 *
 * Preferred over adding a URL to the skip-list below when the SAME endpoint is also called in the
 * foreground: a URL match cannot tell a background poll from a user-initiated load, so it would
 * silence both. The declaration poll is exactly that case — it re-reads the very endpoint the
 * declaration list loads on open.
 *
 * This is not cosmetic. `LoaderSpinnerComponent` reparents the overlay into the topmost open dialog
 * pane, so a polled request while a dialog is open flashes a spinner ACROSS THAT DIALOG. On the
 * signature dialog that meant the embedded ceremony iframe visibly flickered on every poll tick.
 */
export const SKIP_LOADER = new HttpContextToken<boolean>(() => false);

export const loaderInterceptor: HttpInterceptorFn = (req, next) => {
  // Per-request opt-out — see SKIP_LOADER.
  if (req.context.get(SKIP_LOADER)) {
    return next(req);
  }
  // Skip loader for background/polling requests
  if (req.method === 'POST' && req.url === 'api/location/shx-upload') {
    return next(req);
  }
  if (req.url.startsWith('api/admin/document-check/status') || req.url.startsWith('api/admin/document-check/reports')) {
    return next(req);
  }

  const loader = inject(LoadingBarService).useRef();
  const loaderMessage = inject(LoaderMessageService);
  const isAnalysis = req.url.includes('/api/analyze-document');
  const isTranslation = req.url.includes('/api/translate/by-hash');
  const isDocumentUpload = req.method === 'POST' && req.url === 'api/document' && req.body instanceof FormData;
  if (isAnalysis) loaderMessage.set('analyzing');
  else if (isTranslation) loaderMessage.set('translating');
  else if (isDocumentUpload) loaderMessage.set('uploading');
  loader.start();
  return next(req).pipe(
    finalize(() => {
      loader.complete();
      if (isAnalysis || isTranslation || isDocumentUpload) loaderMessage.set(null);
    })
  );
};
